import DOMPurify from 'dompurify'

// A message body's bodyContentType (migration 0045) is 'html' only for a
// channel that actually sends HTML (M365 email) — everything else renders as
// plain text, unchanged. Raw email HTML is untrusted, attacker-reachable
// input (unlike templatePreview.js's admin-authored templates, this is
// customer/vendor-sourced) and is stored, so an unsanitized render would be a
// persistent XSS vector reaching every agent who opens the conversation.
// DOMPurify only — see docs/decisions.md (2026-08-18) for why this does NOT
// also run PII redaction: redaction in this app guards the AI-model-call
// boundary, never what an agent sees: every other message body has always
// rendered raw/unredacted, and this shouldn't quietly introduce a different,
// broader policy as a side effect of rendering HTML safely.
// ADD_DATA_URI_TAGS: ['img'] — DOMPurify's default config strips data: URIs
// from every attribute (its own ALLOWED_URI_REGEXP doesn't include that
// scheme), which would otherwise render an outbound reply's own inline
// image (RichReplyEditor.jsx's "Insert image") as a broken image here, even
// though the sent message really does carry it — see send-approved-reply.js's
// own comment on why the ORIGINAL data: URI (not the cid:-rewritten Graph
// copy) is what gets recorded. Scoped to img only — an <a href="data:...">
// is still stripped, same as today.
//
// Extracted from App.jsx (where message bodies are rendered) so
// SuggestionPanel.jsx's translated-draft preview can reuse the exact same
// sanitization without App.jsx and SuggestionPanel.jsx importing from each
// other (App.jsx already imports SuggestionPanel.jsx).
//
// Links open in a new tab (2026-10-12): a link in a message body (e.g. the
// "View Order" link at the bottom of an automated failure notice) must never
// navigate the CXHub tab away. After sanitizing, every <a> with an http(s)
// href gets target="_blank" and rel="noopener noreferrer"; rel is required
// because these bodies are untrusted, so the opened page must not get
// window.opener. An existing target or rel is overwritten, so a body can't
// opt out. mailto: and tel: links are left as they are (a new tab for them
// only opens a blank tab). What DOMPurify allows or strips is unchanged.
//
// Scoped to this function by post-processing the sanitized result
// (RETURN_DOM_FRAGMENT, then a pass over its links), NOT a DOMPurify hook: a
// hook is global to the shared DOMPurify instance and would also change
// templatePreview.js.
const NEW_TAB_HREF = /^\s*https?:/i

export function applyNewTabToLinks(root) {
  for (const a of root?.querySelectorAll?.('a[href]') ?? []) {
    if (!NEW_TAB_HREF.test(a.getAttribute('href') ?? '')) continue
    a.setAttribute('target', '_blank')
    a.setAttribute('rel', 'noopener noreferrer')
  }
  return root
}

// System notices (2026-10-12): automated return notices (eBay "Automated
// Return Needs Review" and the Site Automation notices) are shown exactly as
// received, and two things are done to them that no other email gets, both
// AFTER sanitizing (never instead of it), so sanitization itself stays
// identical for every email:
//   - content hidden by an inline style is SHOWN (these emails hide lines
//     like "Customer purchased calltag: No" in a display:none span, and
//     agents need them). Only the hiding declarations are removed; the text
//     keeps its place, with no label or colour of its own.
//   - text hidden by a white or near-white colour (2026-11-04) is shown in
//     the message's normal text colour. An element whose inline colour (a
//     style color, or a <font color>) has a lightness of 90% or more loses
//     that colour, so it inherits the message's own (and follows dark mode),
//     unless its nearest inline background (a style background or
//     background-color, or a bgcolor attribute, on itself or an ancestor
//     within the notice) is a real dark one: white text on a coloured banner
//     keeps its colours. A light background (a white wrapper table) counts as
//     no background, since the text is invisible on it too.
//   - a remote 1x1 image (an email-open tracking pixel, e.g.
//     http://sendmail.1aauto.com/wf/open?...) is dropped, so viewing the
//     contact doesn't report an "open".
// Every other email keeps hidden content hidden and renders every image.
const HIDING_DECLARATION = /^\s*(?:display\s*:\s*none|visibility\s*:\s*(?:hidden|collapse)|opacity\s*:\s*0*(?:\.0*)?|(?:max-)?(?:height|width)\s*:\s*0(?:px|em|rem|pt|%)?|font-size\s*:\s*0(?:px|em|rem|pt|%)?|line-height\s*:\s*0(?:px|em|rem|pt|%)?|mso-hide\s*:\s*all)\s*(?:!important)?\s*$/i

export function revealHiddenInlineContent(root) {
  for (const el of root?.querySelectorAll?.('[style]') ?? []) {
    const style = el.getAttribute('style') ?? ''
    const kept = style.split(';').filter((d) => d.trim() && !HIDING_DECLARATION.test(d))
    if (kept.length === style.split(';').filter((d) => d.trim()).length) continue
    if (kept.length) el.setAttribute('style', kept.map((d) => d.trim()).join('; '))
    else el.removeAttribute('style')
  }
  revealLightText(root)
  return root
}

const ONE_PX = /^\s*1(?:px)?\s*$/i
function styleValue(style, prop) {
  const m = new RegExp(`(?:^|;)\\s*${prop}\\s*:\\s*([^;]+)`, 'i').exec(style ?? '')
  return m ? m[1].replace(/!important/i, '').trim() : null
}

const LIGHT_NAMED = new Set(['white', 'snow', 'ivory', 'whitesmoke', 'ghostwhite', 'floralwhite', 'mintcream', 'azure', 'honeydew', 'seashell', 'aliceblue', 'lavenderblush', 'oldlace', 'linen', 'lightyellow', 'cornsilk', 'beige'])
const NOT_A_COLOR = /^(?:none|transparent|inherit|initial|unset|currentcolor)$/i

// HSL lightness (0 to 1) of a CSS colour, or null when it can't be read.
export function colorLightness(value) {
  const v = String(value ?? '').trim().toLowerCase()
  if (!v) return null
  if (LIGHT_NAMED.has(v)) return 1
  let rgb = null
  const hex = /^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/.exec(v)
  if (hex) {
    const h = hex[1].length <= 4 ? [...hex[1].slice(0, 3)].map((c) => c + c) : [hex[1].slice(0, 2), hex[1].slice(2, 4), hex[1].slice(4, 6)]
    rgb = h.map((x) => parseInt(x, 16))
  }
  const fn = /^rgba?\(\s*([\d.]+%?)[\s,]+([\d.]+%?)[\s,]+([\d.]+%?)/.exec(v)
  if (fn) rgb = fn.slice(1, 4).map((x) => (x.endsWith('%') ? parseFloat(x) * 2.55 : parseFloat(x)))
  if (rgb) return (Math.max(...rgb) + Math.min(...rgb)) / 2 / 255
  const hsl = /^hsla?\(\s*[\d.]+(?:deg)?[\s,]+[\d.]+%[\s,]+([\d.]+)%/.exec(v)
  if (hsl) return parseFloat(hsl[1]) / 100
  return null
}
const isLight = (value) => (colorLightness(value) ?? 0) >= 0.9

// An element's own inline background (style background-color or background,
// or a bgcolor attribute), or null when it has none.
function ownBackground(el) {
  const style = el.getAttribute?.('style') ?? ''
  const bg = styleValue(style, 'background-color') ?? styleValue(style, 'background') ?? el.getAttribute?.('bgcolor') ?? null
  if (bg == null || NOT_A_COLOR.test(bg.trim())) return null
  return bg
}

// True when the nearest inline background (on the element or an ancestor
// within the notice) is a real, non-light one. An image or a colour that
// can't be read counts as real, so the text keeps its colour.
function onDarkBackground(el, root) {
  for (let node = el; node && node !== root; node = node.parentElement) {
    const bg = ownBackground(node)
    if (bg == null) continue
    const first = bg.trim().split(/\s+(?![^(]*\))/)[0]
    const lightness = colorLightness(first)
    return lightness == null || lightness < 0.9
  }
  return false
}

// Light text (2026-11-04): see the System notices comment above.
function revealLightText(root) {
  for (const el of root?.querySelectorAll?.('[style], font[color]') ?? []) {
    const style = el.getAttribute('style') ?? ''
    const styleLight = isLight(styleValue(style, 'color'))
    const attrLight = isLight(el.getAttribute('color'))
    if (!styleLight && !attrLight) continue
    if (onDarkBackground(el, root)) continue
    if (styleLight) {
      const kept = style.split(';').filter((d) => d.trim() && !/^\s*color\s*:/i.test(d))
      if (kept.length) el.setAttribute('style', kept.map((d) => d.trim()).join('; '))
      else el.removeAttribute('style')
    }
    if (attrLight) el.removeAttribute('color')
  }
}

export function isRemoteTrackingPixel(img) {
  if (!/^\s*https?:/i.test(img.getAttribute('src') ?? '')) return false
  const style = img.getAttribute('style') ?? ''
  const w = img.getAttribute('width') ?? styleValue(style, 'width')
  const h = img.getAttribute('height') ?? styleValue(style, 'height')
  return ONE_PX.test(w ?? '') && ONE_PX.test(h ?? '')
}

export function dropRemoteTrackingPixels(root) {
  for (const img of [...(root?.querySelectorAll?.('img') ?? [])]) {
    if (isRemoteTrackingPixel(img)) img.remove()
  }
  return root
}

// systemNotice: an automated return notice (see above). Without it the
// output is exactly what it has always been.
export function sanitizeHtmlBody(html, { systemNotice = false } = {}) {
  const fragment = DOMPurify.sanitize(html ?? '', { ADD_DATA_URI_TAGS: ['img'], RETURN_DOM_FRAGMENT: true })
  applyNewTabToLinks(fragment)
  if (systemNotice) {
    revealHiddenInlineContent(fragment)
    dropRemoteTrackingPixels(fragment)
  }
  const container = document.createElement('div')
  container.appendChild(fragment)
  return container.innerHTML
}
